import React, {useEffect} from 'react';
import Head from 'next/head';

import Link from '../../components/Link';
import DocumentHeader from '../../components/DocumentHeader';
import {useAnalytics} from '../../hooks';
import {getBrandName} from '../../components/Brand';
import {BASE_URL} from '../../constants/config';
import {lists} from '../JobsListPage/lists';

// One line per list; falls back to a generic line when a new list ships.
const DESCRIPTIONS = {
  fde: 'Every top Forward Deployed Engineer program, ranked, with a page per company and live hiring status.',
  'new-grad': 'The entry-level software engineering programs worth your application, re-verified as the market moves.',
  'investment-banking': 'Top investment banking analyst programs, ranked, with open roles and hiring status.',
  'security-engineer': 'The strongest security engineering orgs, ranked, with a page per company and open roles.',
  'data-engineer': 'The data orgs worth your application, including which companies actually use the title.',
  'data-scientist': 'Top data science programs, ranked, with remote and entry-level paths.',
  'ml-engineer': 'The leading machine learning engineering programs, ranked, with live hiring status.',
};

const ListCard = ({list}) => (
  <Link
    href={`/jobs/${list.slug}?src=jobs`}
    className="group flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:border-indigo-300 hover:shadow-md md:p-6"
  >
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">{list.listName}</p>
      <h2 className="mt-1 text-lg font-semibold text-gray-900 group-hover:text-indigo-700">
        {`${list.roleName} Jobs`}
      </h2>
      <p className="mt-1 text-sm leading-relaxed text-gray-600">
        {DESCRIPTIONS[list.slug] || `A living, ranked index of ${list.roleName} jobs at top companies.`}
      </p>
    </div>
    <span aria-hidden="true" className="shrink-0 text-xl text-gray-300 transition-colors group-hover:text-indigo-600">
      →
    </span>
  </Link>
);

const JobsPage = () => {
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.jobsView({page: 'jobs-index'});
  }, []);

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: lists.map((list, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: `${list.roleName} Jobs`,
      url: `${BASE_URL}/jobs/${list.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <DocumentHeader
        title="Job Boards: Ranked Tech and Finance Job Lists by Role"
        metaDescription={`Browse ${getBrandName()}’s ranked job lists by role: Forward Deployed Engineer, New Grad, Investment Banking, Security, Data, and Machine Learning jobs at top companies.`}
        socialDescription="Ranked job lists by role, with a page per company and live hiring status."
        imgPath="/images/jobs_cover_image.png"
        canonicalUrl={`${BASE_URL}/jobs`}
      />
      <Head>
        <script type="application/ld+json" key="jobs-item-list">
          {JSON.stringify(itemListJsonLd)}
        </script>
      </Head>

      <div className="mx-auto max-w-2xl px-4 pt-10 pb-16 md:pt-16">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
          Job boards
        </h1>
        <p className="mt-3 text-base text-gray-600">
          Stay up-to-date on the top-ranking companies and jobs.
        </p>

        <div className="mt-8 flex flex-col gap-4">
          {lists.map((list) => (
            <ListCard key={list.slug} list={list} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
